import {
  Box,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {
  GangCreationStep,
  GANG_CREATION_STEPS,
  GangCreationFormData,
} from '../../types/gangCreationTypes';
import { useGangLifecycleStore } from '../../stores/gangLifecycleStore';
import { useGangsPanelStore } from '../../stores/gangsPanelStore';
import { useGangLifecycleForm } from '../../hooks/useGangLifecycleForm';
import { GangIdentityStep } from '../organisms/lifecycle/GangIdentityStep';
import { GangLeadersStep } from '../organisms/lifecycle/GangLeadersStep';
import { GangMembersStep } from '../organisms/lifecycle/GangMembersStep';
import { GangMemberConfigurationStep } from '../organisms/lifecycle/GangMemberConfigurationStep';
import { GangReviewStep } from '../organisms/lifecycle/GangReviewStep';
import { useCreateGang, useUpdateGang } from '../../hooks/useGangHooks';
import { CreateGangRequest, UpdateGangRequest } from '../../api/gangsApi';
import {
  buildCreateGangRequest,
  buildUpdateGangRequest,
} from '../../utils/gangRequestTransforms';
import { getErrorMessage } from '@billda/shared-ui';

const createEmptyFormState = (): GangCreationFormData => ({
  name: '',
  isEqualSplit: true,
  participants: {},
  leaderIds: [],
  memberIds: [],
});

interface GangLifecyclePanelProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const getNextStep = (step: GangCreationStep): GangCreationStep =>
  Math.min(step + 1, GangCreationStep.SUMMARY) as GangCreationStep;

const getPreviousStep = (step: GangCreationStep): GangCreationStep =>
  Math.max(step - 1, GangCreationStep.NAME) as GangCreationStep;

export const GangLifecyclePanel = ({
  onSuccess,
  onError,
}: GangLifecyclePanelProps) => {
  const mode = useGangLifecycleStore(state => state.mode);
  const gangId = useGangLifecycleStore(state => state.gangId);
  const activeStep = useGangLifecycleStore(state => state.activeStep);
  const completedSteps = useGangLifecycleStore(state => state.completedSteps);
  const setActiveStep = useGangLifecycleStore(state => state.setActiveStep);
  const markStepCompleted = useGangLifecycleStore(
    state => state.markStepCompleted
  );
  const resetAllState = useGangLifecycleStore(state => state.resetAllState);
  const closePanel = useGangsPanelStore(state => state.closePanel);
  const initialData = useGangLifecycleStore(state => state.initialData);
  const isSubmitting = useGangLifecycleStore(state => state.isSubmitting);
  const setIsSubmitting = useGangLifecycleStore(state => state.setIsSubmitting);

  const {
    form,
    isStepValid,
    isCurrentStepValid,
    hasChanges,
    hasOnlyNameChanged,
    getLeaderParticipants,
    getMemberParticipants,
  } = useGangLifecycleForm(activeStep, initialData);

  const createGangMutation = useCreateGang();
  const updateGangMutation = useUpdateGang();

  const handleNext = () => {
    markStepCompleted(activeStep);
    setActiveStep(getNextStep(activeStep));
  };

  const handlePrevious = () => {
    setActiveStep(getPreviousStep(activeStep));
  };

  const handleStepClick = (targetStep: GangCreationStep) => {
    const isTargetCurrent = targetStep === activeStep;
    const isTargetCompleted = completedSteps.has(targetStep);
    const isNextStep = targetStep === activeStep + 1 && isCurrentStepValid();

    if (isTargetCurrent || isTargetCompleted || isNextStep) {
      if (targetStep > activeStep && isCurrentStepValid()) {
        markStepCompleted(activeStep);
      }
      setActiveStep(targetStep);
    }
  };

  const handleSubmit = () => {
    const { name, isEqualSplit } = form.getValues();
    const trimmedName = name.trim();

    const mutationCallbacks = {
      onSuccess: (data: { name: string } | void) => {
        setIsSubmitting(false);
        resetAllState();
        form.reset(createEmptyFormState());
        closePanel();
        const action = mode === 'create' ? 'created' : 'updated';
        onSuccess?.(
          data && 'name' in data
            ? `Gang "${data.name}" ${action} successfully!`
            : `Gang ${action} successfully!`
        );
      },
      onError: (error: Error) => {
        setIsSubmitting(false);
        onError?.(getErrorMessage(error));
      },
    };

    if (mode === 'create') {
      const leaderParticipants = getLeaderParticipants();
      const memberParticipants = getMemberParticipants();
      const allMembers = [...leaderParticipants, ...memberParticipants];

      const createRequest: CreateGangRequest = buildCreateGangRequest(
        trimmedName,
        isEqualSplit,
        allMembers
      );
      setIsSubmitting(true);
      createGangMutation.mutate(createRequest, mutationCallbacks);
      return;
    }

    if (!gangId) {
      onError?.('Cannot update gang without a gang id.');
      return;
    }

    const leaderParticipants = getLeaderParticipants();
    const memberParticipants = getMemberParticipants();
    const allMembers = [...leaderParticipants, ...memberParticipants];

    const request: UpdateGangRequest = buildUpdateGangRequest(
      gangId,
      trimmedName,
      isEqualSplit,
      allMembers
    );

    setIsSubmitting(true);
    updateGangMutation.mutate(request, mutationCallbacks);
  };

  const hasPendingChanges = hasChanges();

  const handleSaveStep = (step: GangCreationStep) => {
    if (mode !== 'edit' || isSubmitting || !isStepValid(step)) {
      return;
    }
    handleSubmit();
  };

  const renderStepContent = (step: GangCreationStep) => {
    switch (step) {
      case GangCreationStep.NAME:
        return (
          <GangIdentityStep
            form={form}
            mode={mode}
            originalName={initialData?.name ?? null}
            onNext={handleNext}
            disableNext={isSubmitting}
            onSave={() => handleSaveStep(GangCreationStep.NAME)}
            saveDisabled={isSubmitting}
            showSave={hasOnlyNameChanged()}
          />
        );
      case GangCreationStep.LEADERS:
        return (
          <GangLeadersStep
            form={form}
            onNext={handleNext}
            onBack={handlePrevious}
            mode={mode}
            initialLeaderIds={initialData?.leaderIds ?? []}
          />
        );
      case GangCreationStep.MEMBERS:
        return (
          <GangMembersStep
            form={form}
            onNext={handleNext}
            onBack={handlePrevious}
            initialMemberIds={initialData?.memberIds ?? []}
          />
        );
      case GangCreationStep.MEMBER_CONFIG:
        return (
          <GangMemberConfigurationStep
            form={form}
            onNext={handleNext}
            onBack={handlePrevious}
            nextLabel="Next: Review Summary"
          />
        );
      case GangCreationStep.SUMMARY:
        return (
          <GangReviewStep
            form={form}
            mode={mode}
            onBack={handlePrevious}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            hasChanges={hasPendingChanges}
            isValid={isStepValid(GangCreationStep.SUMMARY)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stepper activeStep={activeStep} orientation="vertical">
        {GANG_CREATION_STEPS.map((step, index) => {
          const stepIndex = index as GangCreationStep;
          const isCurrentValid = isCurrentStepValid();
          const isStepComplete = completedSteps.has(stepIndex);
          const isClickable =
            stepIndex === activeStep ||
            isStepComplete ||
            (stepIndex === activeStep + 1 && isCurrentValid);

          return (
            <Step key={step.label}>
              <StepLabel
                sx={{
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  opacity: isClickable ? 1 : 0.6,
                }}
                onClick={() => handleStepClick(stepIndex)}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  sx={{
                    '&:hover': isClickable
                      ? {
                          color: 'primary.main',
                          textDecoration: 'underline',
                        }
                      : {},
                  }}
                >
                  {step.label}
                  {isStepComplete && ' ✓'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent data-testid={`step-content-${index}`}>
                {renderStepContent(stepIndex)}
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default GangLifecyclePanel;
